/**
 * Underwriting Configuration Service
 * Handles all API calls for template management
 */

import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { UW_EMAIL_CHECKBOX_TEMPLATE_ID } from '../constants/underwritingTemplateIds'
import type {
  EmailTemplate,
  ChecklistTemplate,
  MissingInfoFormData,
  DeniedInfoFormData,
  ChecklistSubmitData,
  ChecklistFormData,
} from '../types/underwritingConfig'

/**
 * Fetch checkbox email template by `email_templates.id` (e.g. Missing Info).
 * @throws Error if request fails
 */
export async function getEmailCheckboxTemplateById(
  templateId: number
): Promise<EmailTemplate> {
  try {
    const response = await apiClient.get(`/api/email_templates/${templateId}`)
    return response.data
  } catch (error) {
    throw new Error(
      extractErrorMessage(error) || `Failed to load email template (${templateId})`
    )
  }
}

/**
 * Save checkbox email template (same body shape as Missing Info).
 * @throws Error if request fails
 */
export async function saveEmailCheckboxTemplateById(
  templateId: number,
  data: MissingInfoFormData
): Promise<EmailTemplate> {
  try {
    const response = await apiClient.post(`/api/email_templates/${templateId}`, data)
    return response.data
  } catch (error) {
    throw new Error(
      extractErrorMessage(error) || `Failed to save email template (${templateId})`
    )
  }
}

/**
 * Fetch Missing Info email template
 * @throws Error if request fails
 */
export async function getMissingInfoTemplate(): Promise<EmailTemplate> {
  return getEmailCheckboxTemplateById(UW_EMAIL_CHECKBOX_TEMPLATE_ID.MISSING_INFO)
}

/**
 * Save Missing Info email template
 * @param data - Form data with checkbox_data array
 * @throws Error if request fails
 */
export async function saveMissingInfoTemplate(data: MissingInfoFormData): Promise<EmailTemplate> {
  return saveEmailCheckboxTemplateById(UW_EMAIL_CHECKBOX_TEMPLATE_ID.MISSING_INFO, data)
}

/**
 * Fetch Denied Info email template
 * @throws Error if request fails
 */
export async function getDeniedInfoTemplate(): Promise<EmailTemplate> {
  try {
    const response = await apiClient.get(
      `/api/email_templates/${UW_EMAIL_CHECKBOX_TEMPLATE_ID.DENIED_REASON}`
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load Denied Info template')
  }
}

/**
 * Save Denied Info email template
 * @param data - Form data with checkbox_data array (includes isLock field)
 * @throws Error if request fails
 */
export async function saveDeniedInfoTemplate(data: DeniedInfoFormData): Promise<EmailTemplate> {
  try {
    const response = await apiClient.post(
      `/api/email_templates/${UW_EMAIL_CHECKBOX_TEMPLATE_ID.DENIED_REASON}`,
      data
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save Denied Info template')
  }
}

/**
 * Fetch Applicant Checklist template
 * @throws Error if request fails
 */
export async function getChecklistTemplate(): Promise<ChecklistTemplate> {
  try {
    const response = await apiClient.get('/api/config_applicant_checklist')
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load Checklist template')
  }
}

/**
 * Save Applicant Checklist template
 * Note: API expects data wrapped in {applicant_checkbox: {...}}
 * @param data - Form data with checkbox_data array
 * @throws Error if request fails
 */
export async function saveChecklistTemplate(data: ChecklistFormData): Promise<ChecklistTemplate> {
  try {
    const requestData: ChecklistSubmitData = {
      applicant_checkbox: data,
    }
    const response = await apiClient.post('/api/config_applicant_checklist', requestData)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save Checklist template')
  }
}

/**
 * Fetch Equifax configuration (HTML content for iframe)
 * @param token - Auth token for iframe
 * @throws Error if request fails
 */
export async function getEquifaxConfig(token: string): Promise<string> {
  try {
    // Note: This endpoint requires token in query param due to iframe limitations
    // Fetch happens server-side, token included in URL
    const url = `/api/equifax/config?access_token=${token}`
    const response = await apiClient.get(url)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load Equifax configuration')
  }
}
